<script setup lang="ts">
  import { Floating, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, onBeforeUnmount, ref, watch } from 'vue';
  import { MORE_LABEL, MORE_NAV_LINKS, PRIMARY_NAV_LINKS, type NavLink } from '../config/navLinks';
  import { useViewport, Breakpoint } from '@/shared/composables';

  interface HeaderNavProps {
    primary?: NavLink[];
    more?: NavLink[];
  }

  const props = withDefaults(defineProps<HeaderNavProps>(), {
    primary: () => PRIMARY_NAV_LINKS,
    more: () => MORE_NAV_LINKS,
  });

  const { isSmaller } = useViewport();

  const isAdaptive = isSmaller(Breakpoint.L);

  const primaryList = computed<(NavLink & { isActive: boolean } )[]>(() => {
    const currentUrl = window.location.href;
    const data = props.primary.map((p) => {
      return {
        ...p,
        isActive: currentUrl.includes(p.href),
      }
    });
    if (isAdaptive.value) {
      return data.filter((p) => p.label !== 'Турагентствам' && p.label !== 'Частые вопросы')
    }
    return data;
  });
  const moreList = computed<NavLink[]>(() => {
    if (isAdaptive.value) {
      const res = props.primary.find((p) => p.label === 'Турагентствам' || p.label === 'Частые вопросы');
      return res ? [res, ...props.more] : props.more;
    }
    return props.more;
  });

  const moreOpen = ref(false);

  const toggleMore = (): void => {
    moreOpen.value = !moreOpen.value;
  };

  const closeMore = (): void => {
    moreOpen.value = false;
  };
  const onFloatingHide = (): void => {
    if (moreOpen.value) {
      closeMore();
    }
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      closeMore();
    }
  };

  const onOutsidePointer = (event: MouseEvent): void => {
    const target = event.target as Element | null;
    const inside =
      target != null &&
      (target.closest('[data-testid="site-header-more"]') != null ||
        target.closest('[data-testid="site-header-more-menu"]') != null);
    if (!inside) {
      closeMore();
    }
  };

  watch(
    moreOpen,
    (open) => {
      if (open) {
        document.addEventListener('keydown', onKeydown);
        document.addEventListener('mousedown', onOutsidePointer);
      } else {
        document.removeEventListener('keydown', onKeydown);
        document.removeEventListener('mousedown', onOutsidePointer);
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('mousedown', onOutsidePointer);
  });
</script>

<template>
  <nav
    class="header-nav"
    data-testid="site-header-nav"
  >
    <a
      v-for="link in primaryList"
      :key="link.href"
      :class="['header-nav__link', { 'header-nav__link_active': link.isActive }]"
      data-testid="site-header-nav-link"
      :href="link.href"
      :target="link.external ? '_blank' : undefined"
      :rel="link.external ? 'noopener noreferrer' : undefined"
    >
      <Typo
        :variant="TypoVariant.BodyMRegular"
        tag="span"
      >
        {{ link.label }}
      </Typo>
    </a>

    <Floating
      :visible="moreOpen"
      placement="bottom-start"
      @hide="onFloatingHide"
    >
      <template #reference>
        <button
          type="button"
          class="header-nav__more"
          data-testid="site-header-more"
          @click="toggleMore"
        >
          <Typo
            :variant="TypoVariant.BodyMRegular"
            tag="span"
          >
            {{ MORE_LABEL }}
          </Typo>
        </button>
      </template>

      <template #floating>
        <div
          class="header-nav__more-menu"
          data-testid="site-header-more-menu"
        >
          <a
            v-for="link in moreList"
            :key="link.href"
            class="header-nav__more-link"
            data-testid="site-header-more-link"
            :href="link.href"
            :target="link.external ? '_blank' : undefined"
            :rel="link.external ? 'noopener noreferrer' : undefined"
          >
            <Typo
              :variant="TypoVariant.BodyMRegular"
              tag="span"
            >
              {{ link.label }}
            </Typo>
          </a>
        </div>
      </template>
    </Floating>
  </nav>
</template>

<style scoped lang="scss">
  .header-nav {
    align-items: center;
    display: flex;
    gap: var(--spacing-m);

    &__link {
      color: var(--color-text-on-brand);
      text-decoration: none;

      &_active {
        box-shadow: inset 0 -1px 0 0 white;
      }

      &:hover {
        opacity: 0.8;
      }
    }

    &__more {
      background: none;
      border: none;
      color: var(--color-text-on-brand);
      cursor: pointer;
      padding: 0;

      &:hover {
        opacity: 0.8;
      }
    }

    &__more-menu {
      background-color: var(--color-bg-primary);
      border-radius: var(--rounding-s);
      box-shadow: var(--shadow-m);
      box-sizing: border-box;
      padding: var(--spacing-xs) 0;
      width: 260px;
    }

    &__more-link {
      align-items: center;
      color: var(--color-text-primary);
      display: flex;
      min-height: 32px;
      padding: 0 var(--spacing-s);
      text-decoration: none;

      &:hover {
        background-color: var(--color-bg-secondary);
      }
    }
  }
</style>
